/**
 * features/reports/TeacherDepartmentAnalysis.tsx
 * ------------------------------------------------------------------
 * Teacher & Department Analysis — PER FORM.
 *
 * Pick a FORM and the screen analyses every (teacher × subject)
 * teaching group at that form. A teacher's classes within the form are
 * merged into one group (studentId is globally unique, so merging the
 * per-class reportMarks docs per subject is safe), so the default
 * "by Subject" grouping lines up every teacher who teaches a subject at
 * that level side by side — the comparison teacher analysis is for.
 *
 * Two tabs:
 *   1. Teacher Analysis — group by subject / teacher / department / none
 *   2. Department Analytics — per-department summary, HOD-scoped
 *
 * CSV + branded PDF export (shared autotable chrome). HOD sees only
 * their department(s) on the Department tab.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  Users,
  Building2,
  Download,
  FileText,
  Layers,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  getFormLevel,
  getPassMark,
  termUsesExam,
} from '@/lib/grading';
import {
  buildTeacherGroupStats,
  groupStats,
  statsToTable,
  filterByTeachers,
  GRADE_ORDER,
  type TeacherGroupStat,
  type GroupBy,
} from '@/lib/teacherAnalysis';
import { getAccessibleClasses, curriculumDepartmentsFor } from '@/lib/permissions';
import {
  allDepartments as allDepts,
  isDeptVisible,
  sameDept,
} from '@/lib/departments';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildTeacherAnalysisPdf } from '@/lib/analysisPdf';
import HodAnalysisTab from '@/features/reports/HodAnalysisTab';
import {
  GradientHero,
  SoftCard,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { TermKey, termLabel } from '@/lib/school.config';
import type { MarksDocument, SubjectMarkEntry } from '@/types';

type AnalysisTab = 'hod' | 'teacher' | 'department';

export default function TeacherDepartmentAnalysis({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { activeStudents: students, subjects, classes, teachers, loading } = useData();
  const { userData } = useAuth();

  const [analysisTab, setAnalysisTab] = useState<AnalysisTab>('hod');
  const [formLevel, setFormLevel] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [teacherDeptFilter, setTeacherDeptFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('subject');
  const [basis, setBasis] = useState<'ca' | 'exam'>(termUsesExam(term) ? 'exam' : 'ca');
  useEffect(() => { setBasis(termUsesExam(term) ? 'exam' : 'ca'); }, [term]);
  const [markDocs, setMarkDocs] = useState<MarksDocument[]>([]);
  const [busy, setBusy] = useState(false);

  const myClasses = useMemo(
    () => getAccessibleClasses(userData, classes, teachers, students),
    [userData, classes, teachers, students],
  );

  // Forms the user can analyse (derived from their accessible classes).
  const formLevels = useMemo(() => {
    const set = new Set<string>();
    myClasses.forEach((c) => { const f = getFormLevel(c.name); if (f) set.add(f); });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [myClasses]);

  const formClassIds = useMemo(
    () => myClasses.filter((c) => getFormLevel(c.name) === formLevel).map((c) => c.id),
    [myClasses, formLevel],
  );

  // HOD department scope — resolved via the SAME multi-role-aware resolver
  // Student Analysis "By Subject" uses (curriculumDepartmentsFor), NOT the old
  // isHod = userRole === 'hod' gate. That gate silently failed every HOD in the
  // school, because all of them carry 'hod' only in roles[] with a non-'hod'
  // primary role. null = admin/head wildcard; non-empty = the HOD's departments
  // (with linked-teacher fallback); [] = everyone else.
  const myDepartments = useMemo(
    () => curriculumDepartmentsFor(userData, teachers),
    [userData, teachers],
  );

  // FINDING A: normalise [] -> null (wildcard) on THIS screen, unlike
  // CompositeMarksheet. Non-HODs (editor/hoy/classTeacher/subjectTeacher) resolve
  // to [] and MUST see everything, not a blank screen. This preserves today's
  // deptScope semantics exactly — only the mis-primaried HODs change, from
  // wrongly-wildcard to their real departments.
  const deptScope = useMemo<string[] | null>(
    () => (myDepartments && myDepartments.length ? myDepartments : null),
    [myDepartments],
  );

  // All departments — canonical list (lib/departments): union of teacher
  // departments and every subject's resolved department, deduped.
  const allDepartments = useMemo(
    () => allDepts(subjects, teachers),
    [subjects, teachers],
  );

  // Departments this user can see (mirror of CompositeMarksheet's
  // availableDepartments, keyed on the wildcard-normalised deptScope).
  const visibleDepartments = useMemo(() => {
    if (deptScope === null) return allDepartments;
    const scoped = allDepartments.filter((d) => isDeptVisible(d, deptScope));
    return scoped.length ? scoped : deptScope;
  }, [deptScope, allDepartments]);

  // Teacher-tab department filter default/reconcile. When there is no "All"
  // option (a single-department HOD), force-select that department so their
  // export can never reach outside scope. Otherwise treat empty as "All" and
  // reset any stale selection that is no longer visible.
  useEffect(() => {
    const hasAll = deptScope === null || visibleDepartments.length > 1;
    if (!hasAll) {
      const only = visibleDepartments[0] ?? '';
      if (only && !sameDept(teacherDeptFilter, only)) setTeacherDeptFilter(only);
    } else if (teacherDeptFilter && !visibleDepartments.some((d) => sameDept(d, teacherDeptFilter))) {
      setTeacherDeptFilter('');
    }
  }, [deptScope, visibleDepartments, teacherDeptFilter]);

  // Load marks for every class in the selected form.
  const loadMarks = useCallback(async () => {
    if (formClassIds.length === 0) { setMarkDocs([]); return; }
    setBusy(true);
    try {
      const all: MarksDocument[] = [];
      for (let i = 0; i < formClassIds.length; i += 30) {
        const chunk = formClassIds.slice(i, i + 30);
        const snap = await getDocs(
          query(collection(db, COLLECTIONS.marks), where('year', '==', year), where('term', '==', term), where('classId', 'in', chunk)),
        );
        snap.docs.forEach((d) => all.push(d.data() as MarksDocument));
      }
      setMarkDocs(all);
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  }, [formClassIds, year, term]);

  useEffect(() => { loadMarks(); }, [loadMarks]);

  // Build marks index for the analysis lib. A form has one reportMarks
  // doc per (class, subject); MERGE their entries per subject so a
  // teacher's groups across classes are analysed together. studentId is
  // globally unique, so the merge can never collide.
  const marksBySubject = useMemo(() => {
    const idx: Record<string, Record<string, SubjectMarkEntry>> = {};
    for (const doc of markDocs) {
      idx[doc.subjectId] = { ...(idx[doc.subjectId] ?? {}), ...doc.entries };
    }
    return idx;
  }, [markDocs]);

  const passMark = getPassMark(formLevel);

  const formStudents = useMemo(
    () => students.filter((s) => formClassIds.includes(s.currentClass)),
    [students, formClassIds],
  );

  // Build stats over the whole form.
  const allStats = useMemo(() => {
    if (!formLevel || formStudents.length === 0) return [];
    return buildTeacherGroupStats({
      term, passMark, basisOverride: basis, students: formStudents, subjects, teachers, marksBySubject,
    });
  }, [formLevel, formStudents, term, passMark, subjects, teachers, marksBySubject, basis]);

  // Stats narrowed to the user's department scope (deptScope). null scope
  // (admin/head/non-HOD) passes everything through; a real HOD is limited to
  // their departments here, so the teacher tab, grouping, CSV and PDF all
  // inherit the scope from this single source.
  const scopedStats = useMemo(
    () => allStats.filter((s) => isDeptVisible(s.subjectDepartment || 'Unassigned', deptScope)),
    [allStats, deptScope],
  );

  // Teacher choices for the filter
  const teacherChoices = useMemo(() => {
    const map = new Map<string, string>();
    scopedStats.forEach((s) => { if (s.teacherId !== '__unassigned__') map.set(s.teacherId, s.teacherName); });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [scopedStats]);

  // Filtered stats for teacher tab
  const teacherStats = useMemo(() => {
    let stats = scopedStats;
    if (teacherFilter) stats = filterByTeachers(stats, [teacherFilter]);
    if (teacherDeptFilter) stats = stats.filter((s) => sameDept(s.subjectDepartment || 'Unassigned', teacherDeptFilter));
    return stats;
  }, [scopedStats, teacherFilter, teacherDeptFilter]);

  const grouped = useMemo(() => groupStats(teacherStats, groupBy), [teacherStats, groupBy]);

  // Department-level aggregation
  const departmentStats = useMemo(() => {
    const deptMap: Record<string, {
      department: string;
      teachers: Set<string>;
      totalStudents: number;
      totalGraded: number;
      totalPass: number;
      totalMarks: number;
      subjects: Set<string>;
    }> = {};

    for (const stat of allStats) {
      // Canonical: a result belongs to its SUBJECT's department.
      const dept = stat.subjectDepartment || 'Unassigned';
      if (!isDeptVisible(dept, deptScope) && dept !== 'Unassigned') continue;
      if (departmentFilter && !sameDept(dept, departmentFilter)) continue;

      if (!deptMap[dept]) {
        deptMap[dept] = { department: dept, teachers: new Set(), totalStudents: 0, totalGraded: 0, totalPass: 0, totalMarks: 0, subjects: new Set() };
      }
      const d = deptMap[dept];
      d.teachers.add(stat.teacherId);
      d.subjects.add(stat.subjectId);
      d.totalStudents += stat.total;
      d.totalGraded += stat.graded;
      d.totalPass += stat.passCount;
      if (stat.average !== null) d.totalMarks += stat.average * stat.graded;
    }

    return Object.values(deptMap).map((d) => ({
      ...d,
      teacherCount: d.teachers.size,
      subjectCount: d.subjects.size,
      average: d.totalGraded > 0 ? Math.round((d.totalMarks / d.totalGraded) * 10) / 10 : null,
      passRate: d.totalGraded > 0 ? Math.round((d.totalPass / d.totalGraded) * 100) : null,
    })).sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
  }, [allStats, teachers, visibleDepartments, deptScope, departmentFilter]);

  // Per-teacher within department
  const teachersByDepartment = useMemo(() => {
    const map: Record<string, TeacherGroupStat[]> = {};
    for (const stat of allStats) {
      const dept = stat.subjectDepartment || 'Unassigned';
      if (departmentFilter && !sameDept(dept, departmentFilter)) continue;
      if (!isDeptVisible(dept, deptScope) && dept !== 'Unassigned') continue;
      if (!map[dept]) map[dept] = [];
      map[dept].push(stat);
    }
    return map;
  }, [allStats, teachers, visibleDepartments, deptScope, departmentFilter]);

  // ASCII-safe department tag for scoped export filenames (empty when no
  // specific department is selected -> current form-only name is kept).
  const deptTag = teacherDeptFilter
    ? teacherDeptFilter.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    : '';
  const exportBase = `Teacher_Analysis${deptTag ? '_' + deptTag : ''}_Form_${formLevel}_${term}_${year}`;

  function exportCsv() {
    const table = statsToTable(teacherStats);
    if (table.length === 0) return;
    const esc = (v: string | number) => typeof v === 'string' && v.includes(',') ? `"${v}"` : String(v);
    const csv = table.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportBase}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function exportPdf() {
    if (teacherStats.length === 0) return;
    // Group by subject for the per-form comparison view: each subject
    // section lists the teachers who teach it at this form, side by side.
    const bySubject = groupStats(teacherStats, 'subject');
    const logo = await loadLogoDataUrl();
    buildTeacherAnalysisPdf({
      title: 'TEACHER ANALYSIS',
      metaLine: `${teacherDeptFilter ? teacherDeptFilter + ' - ' : ''}Form ${formLevel}  ·  ${termLabel(term)} ${year}`,
      grades: GRADE_ORDER,
      groups: bySubject.map((g) => ({
        subjectName: g.label,
        rows: g.rows
          .slice()
          .sort((a, b) => (b.average ?? 0) - (a.average ?? 0))
          .map((s) => ({
            teacherName: s.teacherName,
            n: s.graded,
            gradeCounts: s.gradeCounts,
            passRate: s.passRate,
            average: s.average,
          })),
      })),
      logoDataUrl: logo,
      generatedBy: userData?.displayName,
    }).save(`${exportBase}.pdf`);
  }

  if (loading) return <Spinner label="Loading…" />;

  const tabs = [
    { id: 'hod' as const, label: 'HOD Analysis', icon: Layers },
    { id: 'teacher' as const, label: 'Teacher Analysis', icon: Users },
    { id: 'department' as const, label: 'Department', icon: Building2 },
  ];

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Users}
        title="Teacher & Department Analysis"
        subtitle={`${termLabel(term)} ${year} · Per-form performance by teacher and department`}
      />

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setAnalysisTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                analysisTab === t.id ? 'seg-on' : 'seg-off'
              }`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* HOD Analysis — the school's main artifact, department-scoped. */}
      {analysisTab === 'hod' && <HodAnalysisTab year={year} term={term} />}

      {/* Per-form Teacher / Department analysis. */}
      {(analysisTab === 'teacher' || analysisTab === 'department') && (
        <>
          <SoftCard pad="p-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Form</label>
                <select value={formLevel} onChange={(e) => setFormLevel(e.target.value)} className={glassFieldClass('sm:w-56')}>
                  <option value="">— select form —</option>
                  {formLevels.map((f) => <option key={f} value={f}>Form {f}</option>)}
                </select>
              </div>
              {formLevel && allStats.length > 0 && (
                <div className="flex gap-2">
                  <AccentButton variant="soft" icon={Download} onClick={exportCsv}>Export CSV</AccentButton>
                  <AccentButton variant="soft" icon={FileText} onClick={exportPdf}>Export PDF</AccentButton>
                </div>
              )}
            </div>
          </SoftCard>

          {busy && <Spinner label="Loading marks…" />}
          {!busy && !formLevel && (
            <SoftCard><p className="text-sm text-gray-500 text-center py-8">Select a form to view analysis.</p></SoftCard>
          )}
          {!busy && formLevel && allStats.length === 0 && (
            <SoftCard><p className="text-sm text-gray-500 text-center py-4">No marks found for Form {formLevel}.</p></SoftCard>
          )}
          {!busy && formLevel && allStats.length > 0 && (
            analysisTab === 'teacher' ? renderTeacherTab() : renderDepartmentTab()
          )}
        </>
      )}
    </div>
  );

  /* ================================================================
     TEACHER TAB
     ================================================================ */
  function renderTeacherTab() {
    return (
      <div className="space-y-4">
        {/* Filters */}
        <SoftCard pad="p-4">
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Filter by teacher</label>
              <select value={teacherFilter} onChange={(e) => setTeacherFilter(e.target.value)} className={glassFieldClass('sm:w-48')}>
                <option value="">All teachers</option>
                {teacherChoices.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Group by</label>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)} className={glassFieldClass('sm:w-40')}>
                <option value="subject">Subject</option>
                <option value="teacher">Teacher</option>
                <option value="department">Department</option>
                <option value="none">No grouping</option>
              </select>
            </div>
            {termUsesExam(term) && (
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Basis</label>
                <select value={basis} onChange={(e) => setBasis(e.target.value as 'ca' | 'exam')} className={glassFieldClass('sm:w-32')}>
                  <option value="exam">Exam</option>
                  <option value="ca">CA</option>
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Department</label>
              <select value={teacherDeptFilter} onChange={(e) => setTeacherDeptFilter(e.target.value)} className={glassFieldClass('sm:w-48')}>
                {(deptScope === null || visibleDepartments.length > 1) && <option value="">All departments</option>}
                {visibleDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
        </SoftCard>

        {/* Stats table */}
        {grouped.map(({ label, rows }) => (
          <SoftCard key={label} pad="p-0">
            {label && (
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-sm text-gray-800 dark:text-white">{label}</h3>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 text-xs">
                    <th className="px-4 py-2.5 font-medium">Teacher</th>
                    <th className="px-4 py-2.5 font-medium">Subject</th>
                    <th className="px-3 py-2.5 font-medium text-center">Students</th>
                    <th className="px-3 py-2.5 font-medium text-center">Average</th>
                    <th className="px-3 py-2.5 font-medium text-center">Pass Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={`${s.teacherId}-${s.subjectId}`} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50">
                      <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-white">{s.teacherName}</td>
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">{s.subjectName}</td>
                      <td className="px-3 py-2.5 text-center text-gray-600">{s.graded}/{s.total}</td>
                      <td className={`px-3 py-2.5 text-center font-bold ${
                        s.average !== null && s.average >= passMark ? 'text-emerald-600' : s.average !== null ? 'text-red-600' : 'text-gray-400'
                      }`}>{s.average ?? '—'}</td>
                      <td className="px-3 py-2.5 text-center">
                        {s.passRate !== null ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${s.passRate >= 80 ? 'bg-emerald-500' : s.passRate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${s.passRate}%` }} />
                            </div>
                            <span className="text-xs font-medium text-gray-600">{s.passRate}%</span>
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SoftCard>
        ))}
      </div>
    );
  }

  /* ================================================================
     DEPARTMENT TAB
     ================================================================ */
  function renderDepartmentTab() {
    return (
      <div className="space-y-4">
        {/* Department filter */}
        {visibleDepartments.length > 1 && (
          <SoftCard pad="p-4">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Filter by department</label>
            <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} className={glassFieldClass('sm:w-56')}>
              <option value="">All departments</option>
              {visibleDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </SoftCard>
        )}

        {departmentStats.length === 0 ? (
          <SoftCard><p className="text-sm text-gray-500 text-center py-4">No department data available. Assign departments to teachers in the Teachers setup.</p></SoftCard>
        ) : (
          <>
            {/* Department overview cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {departmentStats.map((d) => (
                <SoftCard key={d.department} pad="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={16} className="text-indigo-500" />
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-white">{d.department}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div>
                      <p className="text-xl font-bold text-gray-800 dark:text-white">{d.average ?? '—'}</p>
                      <p className="text-xs text-gray-400">Average</p>
                    </div>
                    <div>
                      <p className={`text-xl font-bold ${d.passRate !== null && d.passRate >= 70 ? 'text-emerald-600' : d.passRate !== null ? 'text-amber-600' : 'text-gray-400'}`}>
                        {d.passRate !== null ? `${d.passRate}%` : '—'}
                      </p>
                      <p className="text-xs text-gray-400">Pass Rate</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{d.teacherCount}</p>
                      <p className="text-xs text-gray-400">Teachers</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{d.subjectCount}</p>
                      <p className="text-xs text-gray-400">Subjects</p>
                    </div>
                  </div>
                  {/* Pass rate bar */}
                  {d.passRate !== null && (
                    <div className="mt-3 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${d.passRate >= 80 ? 'bg-emerald-500' : d.passRate >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${d.passRate}%` }} />
                    </div>
                  )}
                </SoftCard>
              ))}
            </div>

            {/* Per-teacher breakdown within each department */}
            {Object.entries(teachersByDepartment).sort().map(([dept, stats]) => (
              <SoftCard key={dept} pad="p-0">
                <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <Building2 size={14} className="text-indigo-500" />
                  <h3 className="font-semibold text-sm text-gray-800 dark:text-white">{dept}</h3>
                  <span className="text-xs text-gray-400 ml-auto">{new Set(stats.map((s) => s.teacherId)).size} teachers</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 text-xs">
                        <th className="px-4 py-2 font-medium">Teacher</th>
                        <th className="px-4 py-2 font-medium">Subject</th>
                        <th className="px-3 py-2 font-medium text-center">Students</th>
                        <th className="px-3 py-2 font-medium text-center">Average</th>
                        <th className="px-3 py-2 font-medium text-center">Pass Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.sort((a, b) => (b.average ?? 0) - (a.average ?? 0)).map((s) => (
                        <tr key={`${s.teacherId}-${s.subjectId}`} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50">
                          <td className="px-4 py-2 font-medium text-gray-800 dark:text-white">{s.teacherName}</td>
                          <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{s.subjectName}</td>
                          <td className="px-3 py-2 text-center text-gray-600">{s.graded}/{s.total}</td>
                          <td className={`px-3 py-2 text-center font-bold ${s.average !== null && s.average >= passMark ? 'text-emerald-600' : 'text-red-600'}`}>
                            {s.average ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {s.passRate !== null ? (
                              <span className={`text-xs font-medium ${s.passRate >= 80 ? 'text-emerald-600' : s.passRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                {s.passRate}%
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SoftCard>
            ))}
          </>
        )}
      </div>
    );
  }
}
